import React from 'react';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import ComparisonProduct from './ComparisonProduct/ComparisonProduct';
import ComparisonChanger from './ComparisonChanger/ComparisonChanger';
import Viewport from '../../helpers/Viewport';
import Activity from '../../helpers/Activity';
import DataError from '../../components/DataError/DataError';
import './ComparisonModal.css';

function ComparisonModal({
    client,
    catalog,
    category,
    searchQuery,
    products,
    comparisonList,
    comparisonMaxItems,
    taxMultiplier,
    taxLabel,
    taxDisclaimerBackgroundColor,
    taxDisclaimerLabelColor,
    actionButtonBackgroundColor,
    actionButtonBorderColor,
    actionButtonLabelColor,
    secondaryActionButtonBackgroundColor,
    secondaryActionButtonBorderColor,
    secondaryActionButtonLabelColor,
    cancelButtonBackgroundColor,
    cancelButtonBorderColor,
    cancelButtonLabelColor,
    compareGoToProductButtonIcon,
    compareAddProductButtonIcon,
    compareHighlightColor,
    compareChangerSelectionColor,
    compareChangerPreviousSelectionColor,
    discountBackgroundColor,
    discountLabelColor,
    exclusivePriceLabelColor,
    exclusivePriceIcon,
    paginatorActiveItemBackgroundColor,
    paginatorActiveItemLabelColor,
    showAIGeneratedFeatures,
    showAIGeneratedTag,
    AIGeneratedTagLabel,
    AIGeneratedTagBackgroundColor,
    AIGeneratedTagLabelColor,
    AIGeneratedTagBorderColor,
    onClick,
    onChange,
    onRemove,
    onClose,
    getMetadata
}) {
    const borderRadius = {topLeft: 0, topRight: 0, bottomLeft: 50, bottomRight: 50};
    const viewport = Viewport.dimensions;
    const smallModeTreshold = {width: 768, height: 600};
    const [changeMode, setChangeMode] = React.useState(false);
    const [changeIndex, setChangeIndex] = React.useState(-1);
    const [highlightDifferences, setHighlightDifferences] = React.useState(false);
    const productWidth = 300;
    const productPadding = {top: 0, right: 0, bottom: 0, left: 0};
    const productMargin = {top: 10, right: 10, bottom: 10, left: 10};
    const firstElementPaddingLeft = 40;
    const firstElementMarginLeft = 0;
    const lastElementPaddingRight = 40;
    const lastElementMarginRight = 0;

    const inSmallMode = () => {
        return viewport.width <= smallModeTreshold.width || viewport.height <= smallModeTreshold.height;
    };

    const handleClose = () => {
        if (onClose) onClose();
    };

    const handleClick = (index) => {
        const product = comparisonList[index];
        if (product && onClick) onClick(product);
    };

    const handleChange = (index) => {
        const product = comparisonList[index];
        const metadata = getMetadata();
        Activity.log(metadata, 'compare-mode', 'comparison-modal', 'change-start', true, {
            category_id: category ? category.id : null,
            category_name: category ? category.name : null,
            new_product_id: product ? (product.parent ? product.parent : product.id) : null,
            new_product_name: product && product.name ? product.name : null,
            new_variant_id: product && product.parent ? product.id : null,
            new_product_has_3d_model: product && product.has3dModel || false,
            search_query: searchQuery || null
        });
        setChangeMode(true);
        setChangeIndex(index);
    };

    const handleChangeSelect = (newProduct) => {
        onChange(newProduct, changeIndex);
        closeChangeMode();
    };

    const closeChangeMode = () => {
        const product = comparisonList[changeIndex];
        const metadata = getMetadata();
        Activity.log(metadata, 'compare-mode', 'compare-changer', 'change-cancel', true, {
            category_id: category ? category.id : null,
            category_name: category ? category.name : null,
            new_product_id: product ? (product.parent ? product.parent : product.id) : null,
            new_product_name: product && product.name ? product.name : null,
            new_variant_id: product && product.parent ? product.id : null,
            new_product_has_3d_model: product && product.has3dModel || false,
            search_query: searchQuery || null
        });
        setChangeMode(false);
        setChangeIndex(-1);
    };

    const handleRemove = (index) => {
        const product = comparisonList[index];
        if (product && onRemove) onRemove(product.id);
    };

    const handleHightlightChange = (newValue) => {
        const metadata = getMetadata();
        const metricData = {
            category_id: category ? category.id : null,
            category_name: category ? category.name : null,
            products: comparisonList.map((product) => {
                const productId = product.parent ? product.parent : product.id;
                const variantId = product.parent ? product.id : null;
                return {
                    product_id: productId,
                    product_name: product.name,
                    variant_id: variantId,
                    has_3d_model: product.has3dModel || false
                };
            }),
            search_query: searchQuery || null
        };
        Activity.log(metadata, 'comparison-mode', 'comparison-modal', `${(newValue ? 'enable' : 'disable')}-highlights`, true, metricData);
        setHighlightDifferences(newValue);
    };

    const getFeaturesKeys = () => {
        let featuresKeys = [
            {container: undefined, containerType: 'object', attribute: 'normalPrice', name: 'Precio Normal'},
            {container: undefined, containerType: 'object', attribute: 'offerPrice', name: 'Precio Oferta'},
            {container: undefined, containerType: 'object', attribute: 'exclusivePrice', name: 'Precio Exclusivo'}
        ];
        comparisonList.forEach((product) => {
            const validFeatures = product.validFeatures(true, false, showAIGeneratedFeatures);
            validFeatures.forEach((validFeature) => {
                const exists = featuresKeys.find((featureKey) => featureKey === validFeature.name);
                if (!exists) featuresKeys.push({container: 'features', containerType: 'array', attribute: validFeature.name, name: validFeature.name});
            });
        });
        return featuresKeys;
    };

    const productHaveDifferences = (featureKey) => {
        let response = false;
        if (comparisonList.length > 0) {
            const firstProduct = comparisonList[0];
            const firstContainer = featureKey.container ? firstProduct[featureKey.container] : firstProduct;
            let firstValue = undefined;
            if (featureKey.containerType === 'object') {
                firstValue = firstContainer[featureKey.attribute] ? firstContainer[featureKey.attribute].toString().trim() : '-';
            } else if (featureKey.containerType === 'array') {
                const firstFeature = firstContainer.find((f) => f.name === featureKey.attribute);
                firstValue = firstFeature && firstFeature.visible && firstFeature.value ? firstFeature.value.trim() : '-';
            }
            for (let i = 1; i < comparisonList.length; i++) {
                const product = comparisonList[i];
                const container = featureKey.container ? product[featureKey.container] : product;
                let value = undefined;
                if (featureKey.containerType === 'object') {
                    value = container[featureKey.attribute] ? container[featureKey.attribute].toString().trim() : '-';
                } else if (featureKey.containerType === 'array') {
                    const feature = container.find((f) => f.name === featureKey.attribute);
                    value = feature && feature.visible && feature.value ? feature.value.trim() : '-';
                }
                if (firstValue !== value) {
                    response = true;
                    break;
                }
            }
        }
        return response;
    };

    const wrapperStyle = () => {
        const viewport = Viewport.dimensions;
        const listWidth = ((productWidth + productPadding.left + productPadding.right + productMargin.left + productMargin.right) * (comparisonMaxItems - 1)) + ((productWidth + firstElementPaddingLeft + lastElementPaddingRight + firstElementMarginLeft + lastElementMarginRight));
        const allElementsFit = viewport.width >= listWidth;
        let style = {
            display: 'flex',
            alignContent: 'center',
            alignItems: 'flex-start'
        };
        if (allElementsFit) {
            style.width = '100%';
            style.justifyContent = 'center';
        } else {
            style.width = listWidth;
            style.justifyContent = 'flex-start';
        }
        return style;
    };

    const renderProducts = () => {
        let products = [];
        for (let i = 0; i < comparisonMaxItems; i++) {
            const product = comparisonList[i];
            products.push(
                <ComparisonProduct
                    key={`comparison-product-${product ? product.id : `empty-${i}`}`}
                    index={i}
                    product={product}
                    showTableTitle={i === 0}
                    showTableHeaders={i === 0}
                    showHightlightOption={comparisonList.length > 1 && i === comparisonMaxItems - 1}
                    highlightDifferences={comparisonList.length > 1 && highlightDifferences}
                    highlightColor={compareHighlightColor}
                    featuresKeys={getFeaturesKeys()}
                    taxMultiplier={taxMultiplier}
                    taxLabel={taxLabel}
                    taxDisclaimerBackgroundColor={taxDisclaimerBackgroundColor}
                    taxDisclaimerLabelColor={taxDisclaimerLabelColor}
                    actionButtonBackgroundColor={actionButtonBackgroundColor}
                    actionButtonBorderColor={actionButtonBorderColor}
                    actionButtonLabelColor={actionButtonLabelColor}
                    secondaryActionButtonBackgroundColor={secondaryActionButtonBackgroundColor}
                    secondaryActionButtonBorderColor={secondaryActionButtonBorderColor}
                    secondaryActionButtonLabelColor={secondaryActionButtonLabelColor}
                    goToProductButtonIcon={compareGoToProductButtonIcon}
                    addProductButtonIcon={compareAddProductButtonIcon}
                    showAIGeneratedFeatures={showAIGeneratedFeatures}
                    showAIGeneratedTag={showAIGeneratedTag}
                    AIGeneratedTagLabel={AIGeneratedTagLabel}
                    AIGeneratedTagBackgroundColor={AIGeneratedTagBackgroundColor}
                    AIGeneratedTagLabelColor={AIGeneratedTagLabelColor}
                    AIGeneratedTagBorderColor={AIGeneratedTagBorderColor}
                    width={productWidth}
                    paddingTop={productPadding.top}
                    paddingRight={i === comparisonMaxItems - 1 ? lastElementPaddingRight : productPadding.right}
                    paddingBottom={productPadding.bottom}
                    paddingLeft={i === 0 ? firstElementPaddingLeft : productPadding.left}
                    marginTop={productMargin.top}
                    marginRight={i === comparisonMaxItems - 1 ? lastElementMarginRight : productMargin.right}
                    marginBottom={productMargin.bottom}
                    marginLeft={i === 0 ? firstElementMarginLeft : productMargin.left}
                    isDifferent={productHaveDifferences}
                    onClick={handleClick}
                    onChange={handleChange}
                    onRemove={handleRemove}
                    onHighlightChange={handleHightlightChange}
                />
            );
        }
        return products;
    };

    const renderComparison = () => {
        return (
            <React.Fragment>
                <div className='comparison-modal-title'>Comparador de productos</div>
                <div className='comparison-modal-products'>
                    <div className='comparison-modal-products-wrapper' style={wrapperStyle()}>
                        {renderProducts()}
                    </div>
                </div>
                <div className='comparison-modal-footer'>
                    <div className='comparison-modal-footer-button'>
                        <Button
                            icon={undefined}
                            label='Cerrar'
                            labelColor='#D00416'
                            backgroundColor='transparent'
                            borderColor='#D00416'
                            borderRadius={12}
                            disabled={false}
                            onClick={handleClose}
                            boxShadow='none'
                        />
                    </div>
                </div>
            </React.Fragment>
        );
    };

    const renderChanger = () => {
        return (
            <ComparisonChanger
                client={client}
                catalog={catalog}
                category={category}
                searchQuery={searchQuery}
                isAdding={changeIndex < 0 ? true : !comparisonList[changeIndex]}
                products={products}
                comparisonList={comparisonList}
                hoverColor={compareHighlightColor}
                selectionColor={compareChangerSelectionColor}
                previousSelectionColor={compareChangerPreviousSelectionColor}
                actionButtonBackgroundColor={actionButtonBackgroundColor}
                actionButtonBorderColor={actionButtonBorderColor}
                actionButtonLabelColor={actionButtonLabelColor}
                taxMultiplier={taxMultiplier}
                discountBackgroundColor={discountBackgroundColor}
                discountLabelColor={discountLabelColor}
                exclusivePriceLabelColor={exclusivePriceLabelColor}
                exclusivePriceIcon={exclusivePriceIcon}
                paginatorActiveItemBackgroundColor={paginatorActiveItemBackgroundColor}
                paginatorActiveItemLabelColor={paginatorActiveItemLabelColor}
                onClose={closeChangeMode}
                onSelect={handleChangeSelect}
                getMetadata={getMetadata}
            />
        );
    };

    return (
        <Modal mode='top' smallMode={inSmallMode()} borderRadius={borderRadius} padding='0 0 20px 0' width={undefined} height={950} minHeight='unset' maxHeight='100%' showDefaultCloseButton={false} onClose={handleClose}>
            <div className={`comparison-modal${comparisonList.length <= 1 ? ' error' : ''}`}>
                {!changeMode && comparisonList.length <= 1 && (
                    <div className='comparison-modal-error-message'>
                        <DataError
                            message='Deben haber al menos dos productos seleccionados para comparar'
                            actionLabel='Agregar producto'
                            action={() => handleChange(-1)}
                            actionButtonLabelColor={actionButtonLabelColor}
                            actionButtonBackgroundColor={actionButtonBackgroundColor}
                            actionButtonBorderColor={actionButtonBorderColor}
                            secondaryActionLabel='Cerrar'
                            secondaryAction={handleClose}
                            secondaryActionButtonLabelColor={cancelButtonLabelColor}
                            secondaryActionButtonBackgroundColor={cancelButtonBackgroundColor}
                            secondaryActionButtonBorderColor={cancelButtonBorderColor}
                            maxWidth='100%'
                        />
                    </div>
                )}
                {comparisonList.length > 1 && !changeMode && renderComparison()}
                {changeMode && renderChanger()}
            </div>
        </Modal>
    );
}

export default ComparisonModal;